import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dropdown from 'react-bootstrap/Dropdown';
import { LuMenu, LuLogOut, LuWifi, LuWifiOff } from 'react-icons/lu';
import { useSidebar } from '../../contexts/SidebarContext';
import { getData } from '../../services/admin/defaultSetup/esSeriesSetupServices';
import { findTrail, ROLE_LABELS } from './navConfig';
import { clearSession } from '../../utils/session';

const SERIES_REFRESH_MS = 5 * 60 * 1000;

/** Online state + reported downlink speed (Network Information API where available). */
const useNetworkStatus = () => {
  const getConnection = () => navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const [status, setStatus] = useState(() => ({
    online: navigator.onLine,
    downlink: getConnection()?.downlink ?? null,
  }));

  useEffect(() => {
    const connection = getConnection();
    const update = () => setStatus({ online: navigator.onLine, downlink: connection?.downlink ?? null });
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    connection?.addEventListener?.('change', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      connection?.removeEventListener?.('change', update);
    };
  }, []);

  return status;
};

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toggleSidebar, mobileOpen } = useSidebar();
  const role = localStorage.getItem('role');
  const subDomain = localStorage.getItem('sub_domain');
  const network = useNetworkStatus();
  const lastSeriesFetch = useRef(0);

  const roleLabel = ROLE_LABELS[role] || 'User';
  const trail = findTrail(role, pathname) || [];
  const pageTitle = trail[trail.length - 1] || `${roleLabel} Dashboard`;
  const accountName = role === 'event' && subDomain ? `${subDomain}.scientexconference.com` : `${roleLabel} Portal`;

  // Keep the event's active series in localStorage (read by several forms).
  useEffect(() => {
    if (!subDomain || Date.now() - lastSeriesFetch.current < SERIES_REFRESH_MS) return undefined;
    lastSeriesFetch.current = Date.now();
    let cancelled = false;
    getData({ sub_domain: subDomain })
      .then((res) => {
        if (!cancelled && res?.series) localStorage.setItem('series', res.series);
      })
      .catch(() => {
        lastSeriesFetch.current = 0; // retry on the next navigation
      });
    return () => { cancelled = true; };
  }, [subDomain, pathname]);

  useEffect(() => {
    document.title = `${pageTitle} · Scientex Admin`;
  }, [pageTitle]);

  const logOut = () => {
    clearSession();
    navigate('/login', { replace: true });
  };

  const speedLabel = !network.online
    ? 'Offline'
    : network.downlink != null ? `${network.downlink} Mbps` : 'Online';

  return (
    <header className="app-header">
      <button
        type="button"
        className="icon-button d-lg-none"
        onClick={toggleSidebar}
        aria-label="Open navigation"
        aria-controls="app-sidebar"
        aria-expanded={mobileOpen}
      >
        <LuMenu aria-hidden="true" />
      </button>

      <nav className="app-header__titles" aria-label="Breadcrumb">
        <ol className="breadcrumbs">
          {(trail.length ? trail : [pageTitle]).map((crumb, i, all) => (
            <li key={`${crumb}-${i}`} aria-current={i === all.length - 1 ? 'page' : undefined}>{crumb}</li>
          ))}
        </ol>
      </nav>

      <div className="app-header__actions">
        <span
          className="header-chip"
          title={network.online ? 'Network speed reported by your browser' : 'You are offline'}
          role="status"
        >
          {network.online ? <LuWifi aria-hidden="true" /> : <LuWifiOff aria-hidden="true" />}
          <span className={`header-chip__dot${network.online ? '' : ' is-offline'}`} aria-hidden="true" />
          {speedLabel}
        </span>

        <Dropdown align="end">
          <Dropdown.Toggle as="button" type="button" className="user-menu-toggle" aria-label="Account menu">
            <span className="avatar-initials" aria-hidden="true">{roleLabel.charAt(0)}</span>
            <span className="user-menu-toggle__text">
              <span className="user-menu-toggle__name">{roleLabel}</span>
              <span className="user-menu-toggle__role">{accountName}</span>
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Header className="text-truncate" style={{ maxWidth: 260 }}>
              Signed in as <strong>{accountName}</strong>
            </Dropdown.Header>
            <Dropdown.Divider />
            <Dropdown.Item as="button" type="button" onClick={logOut}>
              <LuLogOut aria-hidden="true" /> Log out
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
